import { useCallback, useEffect, useRef } from 'react'
import { useInstantSearch } from 'react-instantsearch'
import { Tabs } from '@backyard-ui/core'

import { ProductTabContent, ServiceTabContent } from './components'
import { useTabContext } from '../../contexts/TabContext'

const TABS = {
  PRODUCTS: 'products',
  SERVICES: 'services'
} as const

const SearchResultTabs = () => {
  const { activeTab, setActiveTab } = useTabContext()
  const { indexUiState } = useInstantSearch()

  const searchQuery = indexUiState?.query || ''
  const previousQuery = useRef(searchQuery)

  /**
   * Implementa a regra de negócio:
   * Toda nova busca sempre começa pela tab de Produtos.
   * Quando a query muda, remove o foco do input (evita reabrir o Autocomplete)
   * e força a tab ativa para Produtos.
   */
  useEffect(() => {
    if (previousQuery.current !== searchQuery) {
      previousQuery.current = searchQuery

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur()
      }

      setActiveTab(TABS.PRODUCTS)
    }
  }, [searchQuery, setActiveTab])

  const handleTabChange = useCallback(
    (value: string) => {
      /**
       * WORKAROUND: O ServicesSearchbar possui 'openOnFocus: true'.
       * Durante a troca de tabs, o navegador pode
       * forçar o foco no input, disparando a abertura indesejada do painel.
       * Desabilitar temporariamente o input impede o foco e o disparo do painel.
       */
      const input = document.getElementById(
        '@autocomplete-quicksearch-input'
      ) as HTMLInputElement | null

      if (input) {
        input.blur()
        input.disabled = true
      }

      setActiveTab(value)

      setTimeout(() => {
        if (input) {
          input.disabled = false
        }
      }, 150)
    },
    [setActiveTab]
  )

  return (
    <Tabs.Root
      value={activeTab}
      onValueChange={handleTabChange}
      UNSAFE_className="bg-[#F8F8F8]">
      <Tabs.List
        aria-label="Escolha entre produtos e serviços"
        UNSAFE_style={{ width: '272px' }}>
        <Tabs.Trigger
          value={TABS.PRODUCTS}
          UNSAFE_className="w-full"
          data-gtm-tab-type="product-search-list-tab">
          Produtos
        </Tabs.Trigger>
        <Tabs.Trigger
          value={TABS.SERVICES}
          UNSAFE_className="w-full"
          data-gtm-tab-type="service-search-list-tab">
          Serviços
        </Tabs.Trigger>
      </Tabs.List>

      <Tabs.Content value={TABS.PRODUCTS} UNSAFE_className="px-0">
        <ProductTabContent />
      </Tabs.Content>
      <Tabs.Content value={TABS.SERVICES} UNSAFE_className="px-0">
        <ServiceTabContent />
      </Tabs.Content>
    </Tabs.Root>
  )
}

export default SearchResultTabs
